<template>
  <div class="reference-link-actions" :class="{ 'reference-link-actions--compact': compact }">
    <span v-if="showStatus" class="reference-link-actions__status" :class="`reference-link-actions__status--${statusTone}`">
      {{ statusText }}
    </span>
    <span v-if="summary" class="reference-link-actions__summary" :title="normalizedUrl">{{ summary }}</span>
    <div class="reference-link-actions__buttons">
      <el-button link :disabled="!canOpen" @click="handleOpen">{{ openLabel }}</el-button>
      <el-button link :disabled="!canCopy" @click="handleCopy">{{ copyLabel }}</el-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ElMessage } from 'element-plus'

import {
  canOpenReferenceUrl,
  copyReferenceUrl,
  getReferenceUrlKind,
  normalizeReferenceUrl,
  openReferenceUrl,
  summarizeReferenceUrl,
} from '@/utils/referenceLinks'

const props = withDefaults(defineProps<{
  url?: string | null
  compact?: boolean
  showStatus?: boolean
  openLabel?: string
  copyLabel?: string
}>(), {
  url: '',
  compact: false,
  showStatus: true,
  openLabel: '打开链接',
  copyLabel: '复制链接',
})

const normalizedUrl = computed(() => normalizeReferenceUrl(props.url))
const summary = computed(() => summarizeReferenceUrl(normalizedUrl.value))
const urlKind = computed(() => getReferenceUrlKind(normalizedUrl.value))
const canOpen = computed(() => canOpenReferenceUrl(normalizedUrl.value))
const canCopy = computed(() => Boolean(normalizedUrl.value))
const statusTone = computed(() => {
  if (urlKind.value === 'external' || urlKind.value === 'inline') {
    return 'ready'
  }
  if (urlKind.value === 'invalid') {
    return 'invalid'
  }
  return 'empty'
})
const statusText = computed(() => {
  if (urlKind.value === 'external') {
    return '链接可用'
  }
  if (urlKind.value === 'inline') {
    return '本地预览'
  }
  if (urlKind.value === 'invalid') {
    return '链接格式无效'
  }
  return '待补充链接'
})

function handleOpen() {
  if (!canOpen.value) {
    ElMessage.warning(urlKind.value === 'invalid' ? '当前链接格式无效，无法打开' : '当前没有可打开的外部链接')
    return
  }

  openReferenceUrl(normalizedUrl.value)
}

async function handleCopy() {
  if (!canCopy.value) {
    ElMessage.warning('当前没有可复制的链接')
    return
  }

  try {
    await copyReferenceUrl(normalizedUrl.value)
    ElMessage.success('链接已复制')
  } catch {
    ElMessage.error('复制失败，请检查浏览器剪贴板权限')
  }
}
</script>

<style scoped>
.reference-link-actions {
  display: grid;
  gap: 8px;
}

.reference-link-actions--compact {
  gap: 6px;
}

.reference-link-actions__status {
  width: fit-content;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
}

.reference-link-actions__status--ready {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.reference-link-actions__status--invalid {
  background: rgba(248, 113, 113, 0.12);
  color: #b91c1c;
}

.reference-link-actions__status--empty {
  background: rgba(148, 163, 184, 0.12);
  color: #64748b;
}

.reference-link-actions__summary {
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
}

.reference-link-actions__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>